/**
 * The replication wire format between a host window (the one connected to
 * the backend) and its replica (fed by the host only). Everything arriving is
 * `unknown` until a guard below says otherwise: another tab, an older build
 * or a stray script may share the origin. What the application says on top -
 * `app` messages - is opaque here and checked by the application's guards.
 */
export const REPLICATION_PROTOCOL = 1

/** Slices as they travel: keyed by name, shape known only to the same build. */
export type RemoteSlices = Readonly<Record<string, unknown>>

/** A plain action the host forwards as it is (`relay` in sync-policy). */
export type RelayedAction = { type: string } & Record<string, unknown>

/**
 * Who a message is for: one link between a host and its replica (a random id
 * per link, so two links never hear each other), and the sender's build - a
 * different build is not trusted.
 */
export type Address = { link: string; build: string }

type Envelope = { v: typeof REPLICATION_PROTOCOL } & Address

type Sequenced = { epoch: string; rev: number }

export type HostBody =
	| ({ kind: 'snapshot'; slices: RemoteSlices } & Sequenced)
	| ({ kind: 'patch'; slices: RemoteSlices } & Sequenced)
	| ({ kind: 'actions'; actions: RelayedAction[] } & Sequenced)
	| { kind: 'app'; payload: unknown }

export type ReplicaBody =
	| { kind: 'hello' }
	| { kind: 'resync' }
	| { kind: 'app'; payload: unknown }

/** A data message of the host: what the replica's sequencer orders. */
export type DataBody = Exclude<HostBody, { kind: 'app' }>

export const wrap = <B>(to: Address, body: B): Envelope & B => ({
	v: REPLICATION_PROTOCOL,
	link: to.link,
	build: to.build,
	...body,
})

export type Opened<B> =
	| { ok: true; body: B }
	| { ok: false; reason: 'not-ours' | 'other-build' }

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value)

const isNumber = (value: unknown): value is number =>
	typeof value === 'number' && Number.isFinite(value)

/**
 * `data` as a message of this link, read by `read`. Anything for another
 * link - or no message at all - is ignored silently; the same link on
 * another build or protocol version is reported, because the window must
 * then be reloaded.
 */
const open = <B>(
	data: unknown,
	to: Address,
	read: (data: Record<string, unknown>) => B | undefined
): Opened<B> => {
	if (!isRecord(data) || data.link !== to.link) {
		return { ok: false, reason: 'not-ours' }
	}
	if (data.v !== REPLICATION_PROTOCOL || data.build !== to.build) {
		return { ok: false, reason: 'other-build' }
	}
	const body = read(data)
	return body === undefined
		? { ok: false, reason: 'not-ours' }
		: { ok: true, body }
}

const isSequenced = (data: Record<string, unknown>): boolean =>
	typeof data.epoch === 'string' && isNumber(data.rev)

const readHostBody = (data: Record<string, unknown>): HostBody | undefined => {
	switch (data.kind) {
		case 'snapshot':
		case 'patch':
			return isSequenced(data) && isRecord(data.slices)
				? (data as HostBody)
				: undefined
		case 'actions':
			return isSequenced(data) &&
				Array.isArray(data.actions) &&
				data.actions.every(
					action => isRecord(action) && typeof action.type === 'string'
				)
				? (data as HostBody)
				: undefined
		case 'app':
			return 'payload' in data ? (data as HostBody) : undefined
		default:
			return undefined
	}
}

const readReplicaBody = (
	data: Record<string, unknown>
): ReplicaBody | undefined => {
	switch (data.kind) {
		case 'hello':
		case 'resync':
			return { kind: data.kind }
		case 'app':
			return 'payload' in data
				? { kind: 'app', payload: data.payload }
				: undefined
		default:
			return undefined
	}
}

export const openHostMessage = (data: unknown, to: Address): Opened<HostBody> =>
	open(data, to, readHostBody)

export const openReplicaMessage = (
	data: unknown,
	to: Address
): Opened<ReplicaBody> => open(data, to, readReplicaBody)
